import "./QuotationView.css";
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toWords } from "number-to-words";
import html2pdf from "html2pdf.js";
import { API_URL } from "../config";
const QuotationView = () => {
  useEffect(() => {
    document.title = "Quotation | Oriflamme IT Solutions";
  }, []);

  const { id } = useParams();
  const navigate = useNavigate();

  const [quotation, setQuotation] = useState(null);
  const [accountDetails, setAccountDetails] = useState(null);
  const num = (v) => Number(v || 0);

  const amountInWords = (num) => {
    return toWords(num).replace(/,/g, "") + " rupees";
  };
  useEffect(() => {
    fetch(`${API_URL}/api/quotation/${id}`, {
      method: "GET",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((res) => {
        if (res.status === 401) {
          navigate("/login");
          return null;
        }

        if (!res.ok) {
          throw new Error("Failed to fetch quotation");
        }

        return res.json();
      })
      .then((data) => {
        if (!data) return;

        if (data.status) {
          setQuotation(data.data);
          setAccountDetails(data.accountDetails);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  }, [id, navigate]);

  const handlePrint = () => {
    const originalTitle = document.title;

    const quotationNumber = `${quotation.bill_customer_name}-${quotation.bill_company_name}-${String(quotation.quotation_number).padStart(6, "0")}`;

    // ✅ Title change
    document.title = quotationNumber;

    // ✅ Print button hide
    const btns = document.getElementById("print-buttons");
    if (btns) btns.style.display = "none";

    // ✅ Print
    window.print();

    // ⏱️ Restore after print
    setTimeout(() => {
      document.title = originalTitle;
      if (btns) btns.style.display = "flex";
    }, 500);
  };

  if (!quotation || !accountDetails)
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary"></div>
        <div className="mt-2">Loading...</div>
      </div>
    );

  return (
    <div className="container py-4 quotation_details">
      <div className="d-flex mb-3 justify-content-end">
        <button className="btn btn-primary" onClick={handlePrint}>
          Print
        </button>
      </div>

      <div id="print-area">
        <table>
          <tbody>
            {/* HEADER */}
            <tr>
              <td colSpan="9" className="text-center">
                <h2>
                  <b>Quotation</b>
                </h2>
              </td>
            </tr>

            {/* COMPANY DETAILS */}
            <tr>
              <td colSpan="9">
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <div style={{ width: "50%" }}>
                    <h3>{accountDetails.company_name}</h3>
                    <div>{accountDetails.address}</div>
                    <div>Ph no. : {accountDetails.phone_number}</div>
                    <div>GST: {accountDetails.gst}</div>
                    <div>E-Mail : {accountDetails.email}</div>
                    <div>Website : {accountDetails.website}</div>
                  </div>
                  <div style={{ width: "50%", textAlign: "right" }}>
                    {accountDetails.logo ? (
                      <img
                        src={`${API_URL}/storage/${accountDetails.logo}`}
                        alt="logo"
                        style={{ width: 200 }}
                      />
                    ) : (
                      "No Logo"
                    )}
                  </div>
                </div>
              </td>
            </tr>

            {/* BILL / SHIP HEADER */}
            <tr>
              <td colSpan="4">
                <h6>Bill to</h6>
              </td>
              <td colSpan="4">
                <h6>Ship to</h6>
              </td>
              <td>
                <h6>Quotation</h6>
              </td>
            </tr>

            {/* BILL / SHIP DATA */}
            <tr>
              <td colSpan="4">
                <div>
                  <strong>To,</strong>
                </div>
                <strong>{quotation.bill_company_name}</strong>
                <div>{quotation.bill_customer_name}</div>
                <div>{quotation.bill_address}</div>
                <div>Contact No : {quotation.bill_contact_number}</div>
                <div>Email : {quotation.bill_email}</div>
                <div>GST : {quotation.bill_gst}</div>
              </td>

              <td colSpan="4">
                <div>
                  <strong>To,</strong>
                </div>
                <strong>{quotation.ship_company_name}</strong>
                <div>{quotation.ship_customer_name}</div>
                <div>{quotation.ship_address}</div>
                <div>Contact No : {quotation.ship_contact_number}</div>
                <div>Email : {quotation.ship_email}</div>
                <div>GST : {quotation.ship_gst}</div>
              </td>

              <td>
                <div>
                  <strong>Quotation No:</strong>
                </div>

                <div>{String(quotation.quotation_number).padStart(6, "0")}</div>

                <strong>Date:</strong>
                <div>
                  {new Date(quotation.quot_date).toLocaleDateString("en-GB")}
                </div>
              </td>
            </tr>

            {/* ITEMS HEADER */}
            <tr>
              <th>S.No</th>
              <th colSpan={2}>Description</th>
              <th>HSN</th>
              <th>Rate</th>
              <th>Qty</th>
              {/* <th>Discount</th> */}
              <th>Amount</th>
              <th>GST</th>
              <th>Total</th>
            </tr>

            {/* ITEMS */}
            {quotation.quotation_items?.map((item, i) => (
              <tr key={i}>
                <td>{i + 1}</td>
                <td colSpan={2}>
                  <h6>{item.product?.product_name}</h6>
                  <small style={{ whiteSpace: "pre-line" }}>
                    {item.description}
                  </small>
                </td>
                <td>{item.product?.hsn}</td>
                <td>{item.quot_rate}</td>
                <td>{item.quot_qty}</td>
                {/* <td>{item.quot_disc}%</td> */}
                <td>{item.quot_net}</td>
                <td>{item.quot_gst_per || 0}%</td>
                <td>{item.quot_amount}</td>
              </tr>
            ))}

            {/* TOTAL SECTION */}
            <tr>
              <td colSpan="9">
                <div className="row">
                  <div className="col-7">
                    <div className="row">
                      <div className="col-6">
                        <strong>Remarks</strong>
                        <p>{quotation.quot_remarks}</p>
                      </div>
                      <div className="col-6">
                        {" "}
                        <strong>For, {accountDetails.company_name}</strong>
                      </div>
                    </div>

                    <div className="row ">
                      <div className="col-6"></div>
                      <div className="col-6 position-relative">
                        <img
                          src={
                              Number(quotation.account_detail_id) === 1
                                ? "https://suggestcrm.com/images/stamp_old.png"
                                : "https://suggestcrm.com/images/stamp_new.png"
                            }
                          alt="stamp"
                          style={{
                            width: "150px",
                            position: "absolute",
                            bottom: "-20px",
                            left: "10px",
                            zIndex: 1,
                          }}
                        />
                        <img
                          src="https://suggestcrm.com/images/signature.png"
                          alt="stamp"
                          style={{
                            width: "150px",
                            position: "absolute",
                            bottom: "-10px",
                            left: "40px",
                            zIndex: 2,
                          }}
                        />{" "}
                        <p className="mt-4">Authorized Signatory</p>
                      </div>
                    </div>
                  </div>

                  <div className="col-5">
                    <div className="row mb-2">
                      <div className="col-6">Sub Total</div>
                      <div className="col-6">
                        {" "}
                        ₹ {num(quotation.net_amount).toFixed(2)}
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-6">CGST</div>
                      <div className="col-6">
                        {" "}
                        ₹ {num(quotation.net_cgst).toFixed(2)}
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-6">SGST</div>
                      <div className="col-6">
                        {" "}
                        ₹ {num(quotation.net_sgst).toFixed(2)}
                      </div>
                    </div>
                    {num(quotation.net_discount) > 0 && (
                      <div className="row mb-2">
                        <div className="col-6">Discount</div>
                        <div className="col-6">
                          {" "}
                          ₹ {num(quotation.net_discount).toFixed(2)}
                        </div>
                      </div>
                    )}
                    {num(quotation.freight_charge) > 0 && (
                      <div className="row mb-2">
                        <div className="col-6">{quotation.freight_comment}</div>
                        <div className="col-6">
                          {" "}
                          ₹ {num(quotation.freight_charge).toFixed(2)}
                        </div>
                      </div>
                    )}

                    <div className="row">
                      <div className="col-6">
                        <h5>Total</h5>
                      </div>
                      <div className="col-6">
                        <h5> ₹ {num(quotation.netTotal_amount).toFixed(2)}</h5>
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td colSpan="9">
                <div>
                  <strong>Amount in words:</strong>
                </div>
                <h5 style={{ textTransform: "capitalize" }}>
                  {amountInWords(Math.floor(quotation.netTotal_amount))}
                </h5>
              </td>
            </tr>

            {/* TERMS */}
            <tr>
              <td colSpan="9">
                <div className="row">
                  <div className="col-7">
                    <strong>Terms & Conditions</strong>
                    <div>
                      <b>Taxes:</b> {quotation.tc_taxes}
                    </div>
                    <div>
                      <b>Payment: </b> {quotation.tc_Payment}
                    </div>
                    <div>
                      <b>Shipment: </b> {quotation.tc_shipment_charges}
                    </div>
                    <div>
                      <b>Delivery: </b> {quotation.tc_delivery_time}
                    </div>
                    <div>
                      <b>Order: </b> {quotation.tc_order_placement}
                    </div>
                    <div>
                      <b>Validity: </b> {quotation.tc_quote_validity}
                    </div>
                  </div>

                  <div className="col-5">
                    <strong>Bank Details</strong>
                    <div>
                      <b>A/C Holder's Name: </b>{" "}
                      {accountDetails.account_holder_name}
                    </div>
                    <div>
                      <b>Bank Name: </b> {accountDetails.bank_name}
                    </div>
                    <div>
                      <b>A/C No.: </b> {accountDetails.account_number}
                    </div>
                    <div>
                      <b>IFSC Code: </b> {accountDetails.ifsc_code}
                    </div>
                    <div>
                      <b>Branch: </b> {accountDetails.branch}
                    </div>
                  </div>
                </div>
              </td>
            </tr>

            {/* FOOTER */}
            <tr>
              <td colSpan="9">
                <h5>Thanking You,</h5>
                <strong>{quotation.user?.name}</strong>
                <div>{quotation.user?.email}</div>
                <div>{quotation.user?.employee?.mobile}</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default QuotationView;
